import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';

import { ExclamationTriangleFill as ExclamationTriangleFillIcon } from '@styled-icons/bootstrap/ExclamationTriangleFill';
import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { Globe as GlobeIcon } from '@styled-icons/boxicons-regular/Globe';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';
import { FileExcel as FileExcelIcon } from '@styled-icons/fa-solid/FileExcel';
import { Refresh as RefreshIcon } from '@styled-icons/material-twotone/Refresh';
import { api } from 'services/api';
import { formatDate } from 'shared/date';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Heading,
  Progress,
  HStack,
  Text,
  Box,
  useMediaQuery,
  Select,
  SimpleGrid,
  Flex,
  Tooltip,
  Icon,
  IconButton,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import SummaryCard from 'components/SummaryCard';
import SummaryContainer from 'components/SummaryContainer';

import useAlert from 'hooks/useAlert';

import { Import } from 'types/import';

import ImportPreviewTable from './ImportPreviewTable';
import { ImportPreview } from './types';

type Status = '*' | 'error' | 'warning' | 'success';

interface ModalBudgetImportOverviewProps extends ModalRootProps {
  data: {
    importId: number;
    fromImportPage?: boolean;
    parentStageId?: number | null;
  };
}

const modalMargin = 120;
const modalMobileFix = 100;

const ModalBudgetImportOverview: React.FC<ModalBudgetImportOverviewProps> = ({
  data: { importId, fromImportPage = false, parentStageId = null },
  handleClose,
  onConfirm,
  ...restProps
}) => {
  const { openAlert } = useAlert();
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const [refetchInterval, setRefetchInterval] = useState(1000 * 1); // 1 second
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const scrollingBody = useRef<HTMLDivElement>(null);
  const targetElement = useRef<HTMLDivElement>(null);

  const [status, setStatus] = useState<Status>('*');

  const [importPreview, setImportPreview] = useState<ImportPreview[]>([]);
  const [summary, setSummary] = useState({
    total: 0,
    success: 0,
    warning: 0,
    error: 0,
  });

  const getImport = useCallback(async () => {
    const { data: responseData } = await api.get(`/import/${importId}`);
    return responseData.data as Import;
  }, [importId]);

  const getImportPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data: responseData } = await api.get(
        `/import/${importId}/preview`,
        {
          params: {
            page: 1,
            per_page: 9999,
            'filter[status]': status === '*' ? undefined : status,
            'filter[_index]': 'import_element.budget',
            ...(status === '*' ? { 'filter[parent_stage]': 'NULL' } : {}),
          },
        },
      );
      setImportPreview(responseData.data);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao obter o preview do arquivo.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [importId, status]);

  const getImportPreviewSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data: responseData } = await api.get(
        `/import/${importId}/preview/summary`,
        {
          params: {
            'filter[_index]': 'import_element.budget',
          },
        },
      );
      setSummary({
        total: responseData.total,
        success: responseData.status?.success || 0,
        warning: responseData.status?.warning || 0,
        error: responseData.status?.error || 0,
      });
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao obter os KPIs da importação.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [importId]);

  const queryImport = useQuery(['Import', importId], getImport, {
    refetchInterval,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (
        data.status !== 'analyzing' &&
        data.status !== 'processing' &&
        data.status !== 'storing'
      ) {
        setRefetchInterval(1000 * 30); // 30 seconds
      }
    },
  });

  const isImporting = queryImport.data?.status === 'processing';
  const isStoring = queryImport.data?.status === 'storing';

  useEffect(() => {
    if (!isImporting) {
      getImportPreview();
      getImportPreviewSummary();
    }
  }, [isImporting, getImportPreview, getImportPreviewSummary]);

  const exportImportErrorsToXLSX = useCallback(async () => {
    if (!importId || !summary.error || summary.error <= 0) {
      return;
    }

    const toastId = toast({
      status: 'info',
      description: 'Gerando relatório de falhas...',
      isClosable: false,
      duration: null,
    });

    try {
      setIsLoadingReport(true);
      await api.get(`/import/${importId}/report`, {
        params: {
          report_type: 'budget_import',
          'filter[status]': 'error',
        },
      });

      toast({
        description: `Relatório solicitado com sucesso. O arquivo será baixado automaticamente assim que estiver pronto. Você também será notificado.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          `Houve um erro ao gerar o relatório do orçamento.`,
        status: 'error',
      });
    } finally {
      if (toastId) toast.close(toastId);
      setIsLoadingReport(false);
    }
  }, [importId, summary.error]);

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (scrollingBody.current && targetElement.current) {
      scrollingBody.current.scrollTo({
        top: targetElement.current.offsetTop - 500,
        behavior: 'smooth',
      });
    }

    try {
      setSaving(true);
      await api.post(`/import/${importId}/persist`, {
        parent_stage_id: parentStageId || undefined,
      });

      toast({
        description: 'Os seu orçamento será importado.',
        status: 'success',
      });

      setRefetchInterval(1000 * 1); // 1 second
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao importar o arquivo.',
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [importId, parentStageId]);

  const handleCheckForWarningsAndConfirm = useCallback(() => {
    if (summary.warning && summary.warning > 0) {
      openAlert({
        type: 'warning',
        size: '3xl',
        title: 'Serviço a preço informado',
        description: () => {
          const plural = summary.warning > 1 ? 's' : '';
          return (
            <Flex flexDirection="column" width="100%">
              <Text>
                Identificamos {summary.warning} serviço{plural} a preço
                informado em sua importação. Você deverá indicar seus valores e
                unidade de medida dentro do orçamento após a consolidação.
              </Text>
            </Flex>
          );
        },
        buttons: ['Cancelar', 'Confirmar'],
        onConfirm: handleConfirm,
      });
    } else {
      handleConfirm();
    }
  }, [openAlert, summary.warning, handleConfirm]);

  const handleCloseModal = (isClosingViaXButton: boolean): void => {
    if (fromImportPage && isClosingViaXButton) {
      handleClose();
      return;
    }

    if (onConfirm) onConfirm();
    handleClose();
  };

  return (
    <Modal scrollBehavior="inside" {...restProps}>
      <ModalOverlay />
      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Resumo do processamento</ModalHeader>

        {queryImport.data?.budget && (
          <ModalHeader
            mt="-6"
            fontSize="md"
            fontStyle="italic"
            display="flex"
            gap={2}
          >
            <Text>
              Orçamento: {queryImport.data.budget.identification.description}
            </Text>
          </ModalHeader>
        )}

        <ModalCloseButton onClick={() => handleCloseModal(true)} />

        <ModalBody ref={scrollingBody}>
          <SummaryContainer loading={loading}>
            <SummaryCard
              idx={1}
              isHidden={!summary.total}
              typeNumber="number"
              data={{
                icon: GlobeIcon,
                title: 'TOTAL',
                number: summary.total || 0,
                formattedNumber: Intl.NumberFormat('pt-BR').format(
                  summary.total || 0,
                ),
                colorSchemeCard: 'gray',
              }}
            />

            <SummaryCard
              idx={1}
              isHidden={!summary.success}
              typeNumber="number"
              data={{
                icon: CheckIcon,
                title: 'SUCESSOS',
                number: summary.success || 0,
                formattedNumber: Intl.NumberFormat('pt-BR').format(
                  summary.success || 0,
                ),
                colorSchemeCard: 'green',
              }}
            />

            <SummaryCard
              idx={1}
              isHidden={!summary.warning}
              typeNumber="number"
              data={{
                icon: ExclamationTriangleFillIcon,
                title: 'ATENÇÃO',
                tooltipLabel: `Serviços a preço informado. Você deverá indicar seus valores e unidade de medida dentro do orçamento após a consolidação.`,
                number: summary.warning || 0,
                formattedNumber: Intl.NumberFormat('pt-BR').format(
                  summary.warning || 0,
                ),
                colorSchemeCard: 'orange',
              }}
            />

            <SummaryCard
              idx={1}
              isHidden={!summary.error}
              typeNumber="number"
              data={{
                icon: CloseIcon,
                title: 'ERROS',
                number: summary.error || 0,
                formattedNumber: Intl.NumberFormat('pt-BR').format(
                  summary.error || 0,
                ),
                colorSchemeCard: 'red',
              }}
            />
          </SummaryContainer>

          <Heading
            ref={targetElement}
            as="h3"
            fontWeight="medium"
            fontSize="lg"
            mb={2}
          >
            {queryImport.data?.file &&
              `Arquivo: "${queryImport.data?.file.name}"`}
          </Heading>

          {queryImport.data?.progress_bar && (
            <Text>
              {isImporting || isStoring
                ? 'Importação iniciada'
                : 'Importação realizada'}{' '}
              {queryImport.data?.progress_bar?.elapsed_for_humans} (
              {formatDate(queryImport.data?.progress_bar?.start_time_iso)})
            </Text>
          )}

          {(isImporting || isStoring) && (
            <Progress
              hasStripe
              isAnimated
              value={queryImport.data?.progress_bar?.progress_percent || 0}
              mt={4}
            />
          )}

          {(isImporting || isStoring) && (
            <HStack mt={2}>
              <Text fontSize="sm" display="flex">
                <Text fontWeight="bold">Tempo restante</Text>:{' '}
                {queryImport.data?.progress_bar?.remaining_for_humans}
              </Text>

              <Text fontSize="sm" display="flex">
                <Text fontWeight="bold">Tempo estimado</Text>:{' '}
                {queryImport.data?.progress_bar?.estimated_for_humans}
              </Text>
            </HStack>
          )}

          <Box marginY="20px">
            <SimpleGrid columns={{ base: 1, md: 2 }} mt={6}>
              <HStack mb={3}>
                {summary.error && summary.error > 0 && (
                  <Box gap={2} display="flex" alignItems="center">
                    <Button
                      colorScheme="green"
                      leftIcon={<Icon as={FileExcelIcon} />}
                      onClick={exportImportErrorsToXLSX}
                      isLoading={isLoadingReport}
                      isDisabled={loading || saving}
                      loadingText="Solicitando relatório"
                      title="Exportar itens com erros"
                    >
                      Exportar itens com erros
                    </Button>

                    <Tooltip
                      hasArrow
                      label="Exporta os itens com erros para que você possa corrigir e reimportar posteriormente. "
                    >
                      <Icon
                        as={InfoCircleFillIcon}
                        className="icon"
                        color="gray.700"
                      />
                    </Tooltip>
                  </Box>
                )}
              </HStack>

              <HStack mb={3} display="flex" justifyContent="flex-end">
                <Text fontSize="smaller">Status:</Text>
                <Select
                  width="auto"
                  defaultValue="*"
                  onChange={(e) => {
                    setStatus(e.target.value as Status);
                  }}
                >
                  {[
                    ['*', 'Todos'],
                    ['success', 'Sucesso'],
                    ['warning', 'Atenção'],
                    ['error', 'Erro'],
                  ].map(([key, description]) => (
                    <option key={key} value={key}>
                      {description}
                    </option>
                  ))}
                </Select>

                <IconButton
                  size="md"
                  aria-label="Atualizar"
                  colorScheme="blue"
                  icon={<Icon as={RefreshIcon} />}
                  onClick={() => {
                    getImportPreview();
                    getImportPreviewSummary();
                  }}
                  isLoading={loading}
                  isDisabled={loading || saving}
                />
              </HStack>
            </SimpleGrid>

            <ImportPreviewTable
              importId={importId}
              data={importPreview}
              loading={loading}
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button
              isDisabled={loading || saving}
              onClick={() => handleCloseModal(true)}
            >
              Fechar
            </Button>

            {queryImport.data?.status === 'success' ? (
              <Button
                isLoading={saving}
                isDisabled={loading || saving}
                colorScheme="green"
                onClick={() => handleCloseModal(false)}
              >
                {fromImportPage ? 'Ir para o orçamento' : 'Salvar e fechar'}
              </Button>
            ) : (
              <Button
                isLoading={saving}
                isDisabled={loading || saving}
                colorScheme="green"
                onClick={handleCheckForWarningsAndConfirm}
                disabled={isImporting}
              >
                Consolidar importação
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetImportOverview;
